import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { FormProvider, useForm, useFormState, useWatch } from 'react-hook-form';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import useFeatureFlags from 'api/useFeatureFlags';
import useOffers from 'api/useOffers';
import { useProduct } from 'api/useProduct';
import {
  EFeatureFlag,
  EPopupSubType,
  ERewardCalendarFormState,
  OfferType
} from 'constants/enums';
import { scrollToFirstErrorField } from 'utils/reactHookFormValidation.utils';
import { EToasterStatus, showToast } from 'utils/showToast';
import { showValidationToast } from 'utils/showValidationToast';
import { OfferFormLayout } from 'views/Offers/components/OfferFormLayout';
import { useOfferDesignOptions } from 'views/Offers/hooks/useOfferDesignOptions';
import { useOfferFormSubmit } from 'views/Offers/hooks/useOfferFormSubmit';
import { DEFAULT_OFFER_DESIGN_EXTERNAL_ID } from 'views/Offers/Offer.consts';
import { checkUniqueness } from 'views/Offers/Offer.utils';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';

import DialogModal from 'components/DialogModal/DialogModal';
import FormSectionsTabs from 'components/FormSectionsTabs';
import { Form } from 'components/ui';
import { Tabs, TabsContent } from 'components/ui/tabs';

import ConfigurationTab from './components/Tabs/ConfigurationTab';
import OfferBehaviorTab from './components/Tabs/OfferBehaviorTab';
import SettingsTab from './components/Tabs/SettingsTab';
import {
  areRewardCalendarWeeksComplete,
  hasZeroQuantityProducts,
  REWARD_CALENDAR_DEFAULT_VALUES,
  REWARD_CALENDAR_OFFERS_LIST_STALE_MS,
  REWARD_CALENDAR_SCHEMA,
  REWARD_CALENDAR_TABS,
  REWARD_CALENDAR_WEEKS_COMPLETE_MESSAGE,
  RewardCalendarFormFields
} from './RewardCalendarForm.consts';
import { IRewardCalendarFormProps } from './RewardCalendarForm.types';
import {
  buildRewardCalendarInitialValues,
  buildRewardCalendarPayload,
  mapOfferToRewardCalendarWeeks,
  mapWeekToPreviewSequence
} from './RewardCalendarForm.utils';
import { buildRewardCalendarValidationResult } from './RewardCalendarForm.validation';
import { RewardCalendarPreview } from './RewardCalendarPreview';

const RewardCalendarForm: FC<IRewardCalendarFormProps> = ({
  edit = false,
  dup = false
}) => {
  const { publisherId } = useParams();
  const { featureFlags, isLoading } = useFeatureFlags({ publisherId });

  if (isLoading) {
    return null;
  }

  if (!featureFlags?.[EFeatureFlag.DASHBOARD_REWARD_CALENDAR]) {
    return <Navigate to="../popups" replace />;
  }

  return <RewardCalendarFormContent edit={edit} dup={dup} />;
};

const RewardCalendarFormContent: FC<IRewardCalendarFormProps> = ({
  edit = false,
  dup = false
}) => {
  const { publisherId, popupId } = useParams();
  const navigate = useNavigate();
  const { getProducts } = useProduct(publisherId);
  const {
    addPopUpOffer,
    updatePopUpOffer,
    getOffer,
    getOffers
  } = useOffers({
    offerId: popupId,
    offerType: OfferType.POPUP,
    offerSubType: EPopupSubType.REWARD_CALENDAR,
    ...(edit || dup
      ? { listStaleTime: REWARD_CALENDAR_OFFERS_LIST_STALE_MS }
      : {})
  });

  const { submitOffer } = useOfferFormSubmit({
    edit,
    dup,
    offerLabel: 'Reward Calendar',
    addOffer: addPopUpOffer,
    updateOffer: updatePopUpOffer,
    onSuccess: (): void => {
      getOffers.refetch();
    }
  });

  const { offerDesignOptions, defaultOfferDesignId: defaultOfferUiId } =
    useOfferDesignOptions({
      publisherId: publisherId || '',
      offerDesignType: OfferDesignTypes.PopUp,
      defaultExternalId: DEFAULT_OFFER_DESIGN_EXTERNAL_ID[OfferType.POPUP],
      requiredOfferUiSubType: EPopupSubType.REWARD_CALENDAR
    });

  const [activeTab, setActiveTab] = useState<ERewardCalendarFormState>(
    ERewardCalendarFormState.GENERAL
  );
  const [activeWeekIndex, setActiveWeekIndex] = useState(0);
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] =
    useState(false);

  const offers = useMemo(() => getOffers.data?.offers || [], [getOffers.data?.offers]);
  const currentOffer = getOffer.data?.result;

  const form = useForm<RewardCalendarFormFields>({
    resolver: zodResolver(REWARD_CALENDAR_SCHEMA),
    mode: 'onChange',
    defaultValues: REWARD_CALENDAR_DEFAULT_VALUES
  });

  const { isDirty } = useFormState({
    control: form.control
  });

  const currentOfferUI = useWatch({
    control: form.control,
    name: 'offerUiId'
  });

  const watchedWeeks = useWatch({
    control: form.control,
    name: 'weeks'
  });

  const activeWeek = watchedWeeks[activeWeekIndex];
  const previewProductsSequence = useMemo(
    () => (activeWeek ? mapWeekToPreviewSequence(activeWeek) : []),
    [activeWeek]
  );

  const isCreateFlow = !edit && !dup && !popupId;
  const createDirtyBaselineDoneRef = useRef(false);

  const watchedName = form.watch('name');
  const watchedPublisherOfferId = form.watch('publisherOfferId');

  const isNameTaken = !checkUniqueness(
    watchedName,
    'name',
    offers,
    currentOffer,
    edit
  );
  const isPublisherOfferIdTaken = !checkUniqueness(
    watchedPublisherOfferId,
    'publisherOfferId',
    offers,
    currentOffer,
    edit
  );

  useEffect(() => {
    if (!popupId && defaultOfferUiId) {
      form.setValue('offerUiId', defaultOfferUiId, {
        shouldDirty: false,
        shouldValidate: true
      });
    }

    if (!((edit || dup) && currentOffer)) return;

    const weeks = mapOfferToRewardCalendarWeeks(currentOffer);

    form.reset(
      buildRewardCalendarInitialValues({
        currentOffer,
        defaultOfferUiId,
        edit,
        dup,
        weeks
      })
    );
    setActiveWeekIndex(0);
  }, [edit, dup, currentOffer, form, popupId, defaultOfferUiId]);

  useEffect(() => {
    if (!isCreateFlow || createDirtyBaselineDoneRef.current) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      if (createDirtyBaselineDoneRef.current) return;
      form.reset(form.getValues());
      createDirtyBaselineDoneRef.current = true;
    }, 0);

    return (): void => {
      window.clearTimeout(timeoutId);
    };
  }, [isCreateFlow, form]);

  const submitRewardCalendar = useCallback(
    (data: RewardCalendarFormFields): void => {
      if (
        !checkUniqueness(data.name, 'name', offers, currentOffer, edit) ||
        !checkUniqueness(
          data.publisherOfferId,
          'publisherOfferId',
          offers,
          currentOffer,
          edit
        )
      ) {
        setActiveTab(ERewardCalendarFormState.GENERAL);
        showToast({
          message:
            'An offer with this name or External ID already exists. Use unique values before saving.',
          status: EToasterStatus.ERROR
        });
        return;
      }
      if (!areRewardCalendarWeeksComplete(data.weeks)) {
        setActiveTab(ERewardCalendarFormState.CONFIGURATION);
        showToast({
          message: REWARD_CALENDAR_WEEKS_COMPLETE_MESSAGE,
          status: EToasterStatus.ERROR
        });
        return;
      }
      submitOffer(buildRewardCalendarPayload({ data }), popupId);
    },
    [submitOffer, popupId, offers, currentOffer, edit]
  );

  const formTitle = useMemo((): string => {
    if (edit) {
      return `Edit Reward Calendar (${watchedName || currentOffer?.name || ''})`;
    }
    if (dup) {
      return 'Duplicate Reward Calendar';
    }
    return 'Create Reward Calendar';
  }, [edit, dup, watchedName, currentOffer?.name]);

  const isFormValid =
    !addPopUpOffer.isLoading && !updatePopUpOffer.isLoading;

  const navigateToFirstError = useCallback(
    (errors: Record<string, unknown>): void => {
      const { firstFieldLocation, toastMessage, toastDescriptions } =
        buildRewardCalendarValidationResult(errors);

      showValidationToast({ message: toastMessage, descriptions: toastDescriptions });

      if (firstFieldLocation) {
        setActiveTab(firstFieldLocation.tab);
        scrollToFirstErrorField(firstFieldLocation.scrollTarget);
      }
    },
    []
  );

  const handleSave = useCallback(async (): Promise<void> => {
    if (hasZeroQuantityProducts(form.getValues('weeks'))) {
      setActiveTab(ERewardCalendarFormState.CONFIGURATION);
      showToast({
        message:
          'One or more products have a quantity of 0. Please set a quantity greater than 0 for every product before saving.',
        status: EToasterStatus.ERROR
      });
      return;
    }
    const isValid = await form.trigger();
    if (!isValid) {
      navigateToFirstError(
        form.formState.errors as Record<string, unknown>
      );
      return;
    }
    submitRewardCalendar(form.getValues());
  }, [form, navigateToFirstError, submitRewardCalendar]);

  const navigateUp = useCallback((): void => {
    navigate('../');
  }, [navigate]);

  const leaveForm = useCallback((): void => {
    setIsUnsavedChangesModalOpen(false);
    navigateUp();
  }, [navigateUp]);

  const handleCloseAttempt = useCallback((): void => {
    if (isDirty) {
      setIsUnsavedChangesModalOpen(true);
      return;
    }
    navigateUp();
  }, [isDirty, navigateUp]);

  const buttonText = edit ? 'Update' : 'Save';

  return (
    <>
      <OfferFormLayout
        title={formTitle}
        mainButtonText={buttonText}
        isFormValid={isFormValid}
        onSave={handleSave}
        onClose={handleCloseAttempt}
        previewContent={
          <RewardCalendarPreview
            publisherId={publisherId}
            currentOfferUI={currentOfferUI}
            productsSequence={previewProductsSequence}
          />
        }
      >
        <FormProvider {...form}>
          <Form {...form}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSave();
              }}
              className="pt-10 pb-10 w-[528px] mx-auto flex flex-col gap-y-9"
            >
              <Tabs
                value={activeTab}
                onValueChange={(value) =>
                  setActiveTab(value as ERewardCalendarFormState)
                }
                className="w-full"
              >
                <FormSectionsTabs
                  currentTab={activeTab}
                  tabs={REWARD_CALENDAR_TABS}
                />
                <TabsContent
                  value={ERewardCalendarFormState.GENERAL}
                  forceMount
                  className="mt-9 data-[state=inactive]:hidden"
                >
                  <SettingsTab
                    offerDesignOptions={offerDesignOptions}
                    isNameTaken={isNameTaken}
                    isPublisherOfferIdTaken={isPublisherOfferIdTaken}
                  />
                </TabsContent>
                <TabsContent
                  value={ERewardCalendarFormState.CONFIGURATION}
                  forceMount
                  className="mt-9 data-[state=inactive]:hidden"
                >
                  <ConfigurationTab
                    getProducts={getProducts}
                    activeWeekIndex={activeWeekIndex}
                    onActiveWeekChange={setActiveWeekIndex}
                    isActive={activeTab === ERewardCalendarFormState.CONFIGURATION}
                  />
                </TabsContent>
                <TabsContent
                  value={ERewardCalendarFormState.OFFER_BEHAVIOR}
                  forceMount
                  className="mt-9 data-[state=inactive]:hidden"
                >
                  <OfferBehaviorTab />
                </TabsContent>
              </Tabs>
            </form>
          </Form>
        </FormProvider>
      </OfferFormLayout>
      {isUnsavedChangesModalOpen && (
        <DialogModal
          isOpen={isUnsavedChangesModalOpen}
          title="Unsaved Changes"
          description={
            "You've made changes that haven't been saved. Leaving now will discard your changes."
          }
          dataTestId="RewardCalendar-UnsavedChangesModal"
          modalActions={{
            onConfirm: {
              label: 'Continue editing',
              action: (): void => {
                setIsUnsavedChangesModalOpen(false);
              },
              testId: 'RewardCalendar-UnsavedChangesModal-continue'
            },
            onClose: {
              label: 'Leave',
              action: leaveForm,
              variant: 'outline',
              testId: 'RewardCalendar-UnsavedChangesModal-leave'
            }
          }}
        />
      )}
    </>
  );
};

export { RewardCalendarForm };
